/**
 * Stable hooks for the Playwright suite in `e2e-test/`.
 *
 * Why `data-testid` at all: the E2E suite locates elements exclusively by test id and
 * asserts visible copy separately. That split is what lets the UI be restyled or
 * re-laid-out without breaking a single test, while a wrong or missing label still fails
 * exactly one, well-named assertion.
 *
 * Two ways to get an id onto an element:
 *
 *   1. Automatically, from a button's own title -- `addNew("Add New Bank")` yields
 *      `btn-header-add-new-bank`. This instruments the whole app through the shared
 *      factories in tableUtils/formUtils without touching 100+ call sites.
 *   2. Explicitly, by passing a `testId`. Always wins over the derived value. Prefer this
 *      for anything a test depends on precisely: page containers, forms, named fields,
 *      tables and primary actions.
 *
 * The attribute is kept in production builds on purpose: the E2E suite exercises a real
 * production bundle, so stripping it would defeat the point. The cost is a few bytes per
 * element and no runtime behaviour.
 */

/** "Add New Bank" -> "add-new-bank". Returns "" for anything unusable. */
export const slugifyTestId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
};

/**
 * Build a test id from a prefix plus either an explicit id or a derived slug.
 * Returns undefined when nothing usable is available, so React omits the attribute
 * entirely rather than rendering `data-testid=""`.
 */
export const buildTestId = (prefix, explicit, derivedFrom) => {
  if (explicit) return explicit;
  const slug = slugifyTestId(derivedFrom);
  if (!slug) return prefix || undefined;
  return prefix ? `${prefix}-${slug}` : slug;
};

/** Spreadable props: `<CButton {...testId("btn", explicit, title)} />`. */
export const testId = (prefix, explicit, derivedFrom) => {
  const value = buildTestId(prefix, explicit, derivedFrom);
  return value ? { "data-testid": value } : {};
};

/** Compose a child id under a parent, e.g. tid("invoice-form", "submit"). */
export const tid = (...parts) => parts.filter(Boolean).join("-");
